import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";
import { Button } from "./button";
import { Input } from "./input";
import { Textarea } from "./textarea";

const isInputGroupControl = (
  element: Element | null,
): element is HTMLInputElement | HTMLTextAreaElement =>
  element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement;

const isButtonTarget = (target: EventTarget | null): boolean =>
  target instanceof Element && target.closest("button") !== null;

/** A scoped adaptation of shadcn's current new-york-v4 input-group container. */
export const InputGroup = ({ className, ...props }: React.ComponentProps<"div">) => (
  <div
    className={cn(
      "group/input-group relative flex w-full items-center rounded-md border border-input shadow-xs transition-[color,box-shadow] outline-none dark:bg-input/30",
      "h-9 min-w-0 has-[>textarea]:h-auto",
      "has-[>[data-align=inline-start]]:[&>input]:pl-2 has-[>[data-align=inline-end]]:[&>input]:pr-2",
      "has-[>[data-align=block-start]]:h-auto has-[>[data-align=block-start]]:flex-col has-[>[data-align=block-start]]:[&>input]:pb-3",
      "has-[>[data-align=block-end]]:h-auto has-[>[data-align=block-end]]:flex-col has-[>[data-align=block-end]]:[&>input]:pt-3",
      "has-[[data-slot=input-group-control]:focus-visible]:border-ring has-[[data-slot=input-group-control]:focus-visible]:ring-[3px] has-[[data-slot=input-group-control]:focus-visible]:ring-ring/50",
      "has-[[data-slot][aria-invalid=true]]:border-destructive has-[[data-slot][aria-invalid=true]]:ring-destructive/20 dark:has-[[data-slot][aria-invalid=true]]:ring-destructive/40",
      className,
    )}
    data-slot="input-group"
    role="group"
    {...props}
  />
);
const addonVariants = cva(
  "flex h-auto cursor-text items-center justify-center gap-2 py-1.5 text-sm font-medium text-muted-foreground select-none group-data-[disabled=true]/input-group:opacity-50 [&>kbd]:rounded-[calc(var(--radius)-5px)] [&>svg:not([class*='size-'])]:size-4",
  {
    variants: {
      align: {
        "inline-start": "order-first pl-3 has-[>button]:ml-[-0.45rem] has-[>kbd]:ml-[-0.35rem]",
        "inline-end": "order-last pr-3 has-[>button]:mr-[-0.45rem] has-[>kbd]:mr-[-0.35rem]",
        "block-start":
          "order-first w-full justify-start px-3 pt-3 group-has-[>input]/input-group:pt-2.5 [.border-b]:pb-3",
        "block-end":
          "order-last w-full justify-start px-3 pb-3 group-has-[>input]/input-group:pb-2.5 [.border-t]:pt-3",
      },
    },
    defaultVariants: { align: "inline-start" },
  },
);
/** A scoped adaptation of shadcn's current new-york-v4 input group addon. */
export const InputGroupAddon = ({
  className,
  align = "inline-start",
  onClick,
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof addonVariants>) => (
  <div
    className={cn(addonVariants({ align }), className)}
    data-align={align}
    data-slot="input-group-addon"
    onClick={(event) => {
      onClick?.(event);
      const control = event.currentTarget.parentElement?.querySelector("input, textarea") ?? null;
      if (
        !event.defaultPrevented &&
        !isButtonTarget(event.target) &&
        isInputGroupControl(control)
      ) {
        control.focus();
      }
    }}
    role="group"
    {...props}
  />
);
const buttonVariants = cva("flex items-center gap-2 text-sm shadow-none", {
  variants: {
    size: {
      xs: "h-6 gap-1 rounded-[calc(var(--radius)-5px)] px-2 has-[>svg]:px-2 [&>svg:not([class*='size-'])]:size-3.5",
      sm: "h-8 gap-1.5 rounded-md px-2.5 has-[>svg]:px-2.5",
      "icon-xs": "size-6 rounded-[calc(var(--radius)-5px)] p-0 has-[>svg]:p-0",
      "icon-sm": "size-8 p-0 has-[>svg]:p-0",
    },
  },
  defaultVariants: { size: "xs" },
});
/** A scoped adaptation of shadcn's current new-york-v4 input group button. */
export const InputGroupButton = ({
  className,
  type = "button",
  variant = "ghost",
  size = "xs",
  ...props
}: Omit<React.ComponentProps<typeof Button>, "size"> & VariantProps<typeof buttonVariants>) => (
  <Button
    className={cn(buttonVariants({ size }), className)}
    data-size={size}
    variant={variant}
    size={size === "xs" ? "xs" : size === "sm" ? "sm" : "icon-sm"}
    type={type}
    {...props}
  />
);
/** Text content within {@link InputGroup}. */
export const InputGroupText = ({ className, ...props }: React.ComponentProps<"span">) => (
  <span
    className={cn(
      "flex items-center gap-2 text-sm text-muted-foreground [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4",
      className,
    )}
    {...props}
  />
);
/** Input control within {@link InputGroup}. */
export const InputGroupInput = ({ className, ...props }: React.ComponentProps<"input">) => (
  <Input
    className={cn(
      "flex-1 rounded-none border-0 bg-transparent shadow-none focus-visible:ring-0 dark:bg-transparent",
      className,
    )}
    data-slot="input-group-control"
    {...props}
  />
);
/** Textarea control within {@link InputGroup}. */
export const InputGroupTextarea = React.forwardRef<
  HTMLTextAreaElement,
  React.ComponentProps<"textarea">
>(({ className, ...props }, ref) => (
  <Textarea
    className={cn(
      "flex-1 resize-none rounded-none border-0 bg-transparent py-3 shadow-none focus-visible:ring-0 dark:bg-transparent",
      className,
    )}
    data-slot="input-group-control"
    ref={ref}
    {...props}
  />
));
InputGroupTextarea.displayName = "InputGroupTextarea";
